'use client';

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/lib/toast';
import { 
  getWorkflowRoleById,
  getWorkflowRoleUsers,
  type WorkflowRoleDetail,
  type User,
  type WorkflowRuleType,
} from '@/services/api/organization';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import {
  ArrowLeft,
  Shield,
  Users as UsersIcon,
  Loader2,
  Mail,
  Phone,
  Building2,
  Code,
} from 'lucide-react';

export default function WorkflowRoleDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const roleId = params.id as string;

  // State
  const [role, setRole] = useState<WorkflowRoleDetail | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // Load data
  useEffect(() => {
    loadRoleData();
  }, [roleId]);

  const loadRoleData = async () => {
    try {
      setLoading(true);
      const [roleData, usersData] = await Promise.all([
        getWorkflowRoleById(roleId),
        getWorkflowRoleUsers(roleId).catch(() => []),
      ]);
      setRole(roleData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load workflow role data:', error);
      toast.error(t.organization.roles.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  // Get rule type label
  const getRuleTypeLabel = (ruleType: WorkflowRuleType) => {
    const labels: Record<WorkflowRuleType, string> = {
      FIXED_USERS: t.organization.roles.ruleTypes.fixedUsers,
      ORGANIZATION_RELATION: t.organization.roles.ruleTypes.orgRelation,
      SYSTEM_ROLE_MAPPING: t.organization.roles.ruleTypes.roleMapping,
      DYNAMIC_SCRIPT: t.organization.roles.ruleTypes.dynamicScript,
    };
    return labels[ruleType] || ruleType;
  };

  // Get rule type color
  const getRuleTypeColor = (ruleType: WorkflowRuleType) => {
    const colors: Record<WorkflowRuleType, string> = {
      FIXED_USERS: 'bg-blue-100 text-blue-800 border-blue-200',
      ORGANIZATION_RELATION: 'bg-green-100 text-green-800 border-green-200',
      SYSTEM_ROLE_MAPPING: 'bg-purple-100 text-purple-800 border-purple-200',
      DYNAMIC_SCRIPT: 'bg-orange-100 text-orange-800 border-orange-200',
    };
    return colors[ruleType] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Format rule config for display
  const formatRuleConfig = (config: Record<string, any>) => {
    return JSON.stringify(config, null, 2);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-500">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!role) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t.organization.roles.notFound}
          </h2>
          <Button onClick={() => router.back()} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.common.back}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permissions={['workflow-role:read']}>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Button onClick={() => router.back()} variant="ghost" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.common.back}
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">{role.name}</h1>
                <Badge variant="secondary" className="text-sm">
                  {role.code}
                </Badge>
                <Badge className={getRuleTypeColor(role.ruleType)}>
                  {getRuleTypeLabel(role.ruleType)}
                </Badge>
              </div>
              {role.description && (
                <p className="text-gray-500 mt-1">{role.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="basic" className="space-y-4">
          <TabsList>
            <TabsTrigger value="basic">
              <Shield className="h-4 w-4 mr-2" />
              {t.organization.basicInfo}
            </TabsTrigger>
            <TabsTrigger value="config">
              <Code className="h-4 w-4 mr-2" />
              {t.organization.roles.ruleConfig}
            </TabsTrigger>
            {role.ruleType === 'FIXED_USERS' && (
              <TabsTrigger value="users">
                <UsersIcon className="h-4 w-4 mr-2" />
                {t.organization.roles.assignedUsers} ({users.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">
                {t.organization.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500">{t.organization.roleName}</label>
                  <p className="mt-1 text-gray-900">{role.name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">{t.organization.roleCode}</label>
                  <p className="mt-1 text-gray-900 font-mono">{role.code}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">{t.organization.roles.ruleType}</label>
                  <div className="mt-1">
                    <Badge className={getRuleTypeColor(role.ruleType)}>
                      {getRuleTypeLabel(role.ruleType)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">{t.organization.roles.usageCount}</label>
                  <p className="mt-1 text-gray-900">{role.workflowCount || 0} {t.organization.roles.workflows}</p>
                </div>
                {role.ruleType === 'FIXED_USERS' && (
                  <div>
                    <label className="text-sm text-gray-500">{t.organization.roles.assignedUserCount}</label>
                    <p className="mt-1 text-gray-900">{users.length}</p>
                  </div>
                )}
                {role.description && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500">{t.organization.description}</label>
                    <p className="mt-1 text-gray-900">{role.description}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-500">
                    {t.common.createdAt}
                  </label>
                  <p className="mt-1 text-gray-900">
                    {new Date(role.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">
                    {t.common.updatedAt}
                  </label>
                  <p className="mt-1 text-gray-900">
                    {role.updatedAt ? new Date(role.updatedAt).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Rule Config Tab */}
          <TabsContent value="config">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">{t.organization.roles.ruleConfig}</h2>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <pre className="text-sm text-gray-900 font-mono overflow-x-auto">
                  {formatRuleConfig(role.ruleConfig)}
                </pre>
              </div>
              
              {/* Rule Type Description */}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">{t.organization.roles.ruleDescription}</h3>
                <p className="text-sm text-blue-700">
                  {role.ruleType === 'FIXED_USERS' && t.organization.roles.ruleDescriptions.fixedUsers}
                  {role.ruleType === 'ORGANIZATION_RELATION' && t.organization.roles.ruleDescriptions.orgRelation}
                  {role.ruleType === 'SYSTEM_ROLE_MAPPING' && t.organization.roles.ruleDescriptions.roleMapping}
                  {role.ruleType === 'DYNAMIC_SCRIPT' && t.organization.roles.ruleDescriptions.dynamicScript}
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* Users Tab (only for FIXED_USERS type) */}
          {role.ruleType === 'FIXED_USERS' && (
            <TabsContent value="users">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">{t.organization.roles.assignedUsers}</h2>
                </div>

                {users.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {t.organization.roles.noAssignedUsers}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => router.push(`/organization/members/${user.id}`)}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {/* Avatar */}
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                              {user.displayName?.[0] || user.username?.[0] || 'U'}
                            </div>
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {user.displayName || user.username}
                              </span>
                              {user.employeeId && (
                                <Badge variant="outline" className="text-xs">
                                  {user.employeeId}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                              {user.department && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="h-3 w-3" />
                                  {user.department.name}
                                </span>
                              )}
                              {user.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {user.email}
                                </span>
                              )}
                              {user.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {user.phone}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Status */}
                          <div>
                            <Badge 
                              className={
                                user.status === 'ACTIVE' 
                                  ? 'bg-green-100 text-green-800 border-green-200' 
                                  : 'bg-gray-100 text-gray-800 border-gray-200'
                              }
                            >
                              {user.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </PermissionGuard>
  );
}
